import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConferenciaService } from '../../../../core/conferencia/conferencia.service';
import { CupomPessoa, CupomSugestao, EventoCupom } from '../../../../core/conferencia/conferencia.model';
import { AuthService } from '../../../../core/auth/auth.service';

const TIPO_MAP: Record<string, string> = { C: 'B2C', B: 'B2B', E: 'Empresa', M: 'Misto' };
const DESC_MAP: Record<string, string> = { P: 'Percentual', F: 'Fixo', R: 'Valor fixo', V: 'Voucher' };
const EVENTO_MAP: Record<string, string> = {
  EMITIDO: 'Emitido', APLICADO: 'Aplicado', CONTA_CANCELADA: 'Conta cancelada', CONTA_ESTORNADA: 'Conta estornada',
  CANCELADA_NA_FILA: 'Conta cancelada na Fila', REATIVADO: 'Reativado', REMOVIDO: 'Removido da conta',
};

@Component({
  selector: 'app-cpf-mode',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './cpf-mode.component.html',
  styleUrl: './cpf-mode.component.scss',
})
export class CpfModeComponent implements OnInit {
  private readonly confSvc = inject(ConferenciaService);
  private readonly auth = inject(AuthService);
  protected readonly isAdmin = this.auth.isAdmin;

  @Input() qInicial = '';
  @Output() qChange = new EventEmitter<string>();

  protected cdPessoaFisica = '';
  protected readonly loading = signal(false);
  protected readonly erro = signal<string | null>(null);
  protected readonly cupons = signal<CupomPessoa[]>([]);
  protected readonly pesquisou = signal(false);

  // Emissao manual (admin-only)
  protected buscaCupom = '';
  protected readonly sugestoes = signal<CupomSugestao[]>([]);
  protected readonly emitindo = signal(false);
  protected readonly reativando = signal(false);
  protected readonly msgEmissao = signal<string | null>(null);
  // Reativacao: confirmacao inline (por cupom) + feedback estilizado (sem confirm() nativo).
  protected readonly confirmarReativarId = signal<number | null>(null);
  protected readonly feedbackReativacao = signal<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  protected readonly confirmarRemoverId = signal<number | null>(null);
  protected readonly historicoId = signal<number | null>(null);
  protected readonly historico = signal<EventoCupom[]>([]);
  protected readonly carregandoHistorico = signal(false);
  protected readonly erroHistorico = signal<string | null>(null);
  protected readonly paginaHistorico = signal(1);
  protected readonly HIST_POR_PAGINA = 5;
  protected readonly totalPaginasHistorico = computed(() =>
    Math.max(1, Math.ceil(this.historico().length / this.HIST_POR_PAGINA)));
  protected readonly historicoPagina = computed(() => {
    const ini = (this.paginaHistorico() - 1) * this.HIST_POR_PAGINA;
    return this.historico().slice(ini, ini + this.HIST_POR_PAGINA);
  });
  protected readonly removendo = signal(false);
  private emitTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly disponiveis = computed(() => this.cupons().filter(c => c.status === 'Disponivel'));
  protected readonly usados = computed(() => this.cupons().filter(c => c.status === 'Usado'));
  protected readonly expirados = computed(() => this.cupons().filter(c => c.status === 'Expirado'));
  // Uso em conta cancelada/estornada (o back marca o status; nao conta como 'Usado').
  protected readonly canceladosEstornados = computed(() =>
    this.cupons().filter(c => c.status === 'Cancelado' || c.status === 'Estornado'));
  protected readonly totalCupons = computed(() => this.cupons().length);

  ngOnInit(): void {
    if (this.qInicial) {
      this.cdPessoaFisica = this.qInicial;
      this.buscar();
    }
  }

  protected buscar(): void {
    const busca = this.cdPessoaFisica.trim();
    if (!busca) {
      this.erro.set('Informe um CPF ou codigo de pessoa fisica.');
      return;
    }
    this.qChange.emit(busca);
    this.loading.set(true);
    this.erro.set(null);
    this.cupons.set([]);
    this.pesquisou.set(true);

    this.confSvc.buscar(busca).subscribe({
      next: res => {
        this.cupons.set(res.data ?? []);
        this.loading.set(false);
      },
      error: e => {
        this.erro.set(`Falha ao buscar cupons: ${e.status ?? ''} ${e.message}`);
        this.loading.set(false);
      },
    });
  }

  protected limpar(): void {
    this.cdPessoaFisica = '';
    this.cupons.set([]);
    this.pesquisou.set(false);
    this.erro.set(null);
    this.buscaCupom = '';
    this.sugestoes.set([]);
    this.msgEmissao.set(null);
    this.qChange.emit('');
  }

  // ---- Emissao manual (admin) ----
  protected onBuscaCupomChange(termo: string): void {
    this.buscaCupom = termo;
    this.msgEmissao.set(null);
    if (this.emitTimer) clearTimeout(this.emitTimer);
    if (termo.trim().length < 3) { this.sugestoes.set([]); return; }
    this.emitTimer = setTimeout(() => {
      this.confSvc.buscarCupons(termo.trim(), 10, true).subscribe({
        next: res => this.sugestoes.set(res.data ?? []),
        error: () => this.sugestoes.set([]),
      });
    }, 300);
  }

  protected emitir(cupom: CupomSugestao): void {
    const busca = this.cdPessoaFisica.trim();
    if (!busca) return;
    this.emitindo.set(true);
    this.msgEmissao.set(null);
    this.confSvc.emitirManual(busca, cupom.nrSequencia).subscribe({
      next: () => {
        this.emitindo.set(false);
        this.msgEmissao.set(`Cupom ${cupom.codCupom} concedido com sucesso.`);
        this.buscaCupom = '';
        this.sugestoes.set([]);
        this.buscar();
      },
      error: e => {
        this.emitindo.set(false);
        this.msgEmissao.set(e?.error?.error?.message ?? 'Falha ao conceder o cupom.');
      },
    });
  }

  protected pedirReativar(c: CupomPessoa): void {
    this.feedbackReativacao.set(null);
    this.confirmarReativarId.set(c.nrSeqCupomPaciente);
  }

  protected cancelarReativar(): void {
    this.confirmarReativarId.set(null);
  }

  protected confirmarReativar(c: CupomPessoa): void {
    this.reativando.set(true);
    this.confSvc.reativar(c.nrSeqCupomPaciente).subscribe({
      next: () => {
        this.reativando.set(false);
        this.confirmarReativarId.set(null);
        this.feedbackReativacao.set({ tipo: 'ok', texto: `Cupom ${c.codCupom} reativado com sucesso.` });
        this.buscar();
      },
      error: e => {
        this.reativando.set(false);
        this.confirmarReativarId.set(null);
        this.feedbackReativacao.set({ tipo: 'erro', texto: e?.error?.error?.message ?? 'Falha ao reativar o cupom.' });
      },
    });
  }

  protected pedirRemover(c: CupomPessoa): void {
    this.feedbackReativacao.set(null);
    this.confirmarReativarId.set(null);
    this.confirmarRemoverId.set(c.nrSeqCupomPaciente);
  }

  protected cancelarRemover(): void {
    this.confirmarRemoverId.set(null);
  }

  protected confirmarRemover(c: CupomPessoa): void {
    this.removendo.set(true);
    this.confSvc.removerDaConta(c.nrSeqCupomPaciente).subscribe({
      next: r => {
        this.removendo.set(false);
        this.confirmarRemoverId.set(null);
        this.feedbackReativacao.set({ tipo: 'ok', texto: r?.data?.dsResultado || `Cupom ${c.codCupom} removido da conta.` });
        this.buscar();
      },
      error: e => {
        this.removendo.set(false);
        this.confirmarRemoverId.set(null);
        this.feedbackReativacao.set({ tipo: 'erro', texto: e?.error?.error?.message ?? 'Falha ao remover o cupom da conta.' });
      },
    });
  }

  protected alternarHistorico(c: CupomPessoa): void {
    if (this.historicoId() === c.nrSeqCupomPaciente) {
      this.historicoId.set(null);
      return;
    }
    this.historicoId.set(c.nrSeqCupomPaciente);
    this.historico.set([]);
    this.paginaHistorico.set(1);
    this.erroHistorico.set(null);
    this.carregandoHistorico.set(true);
    this.confSvc.historico(c.nrSeqCupomPaciente).subscribe({
      next: r => {
        this.carregandoHistorico.set(false);
        // Mais recente primeiro: o que se procura costuma ser o ultimo uso.
        this.historico.set([...(r?.data ?? [])].reverse());
      },
      error: e => {
        this.carregandoHistorico.set(false);
        this.erroHistorico.set(e?.error?.error?.message ?? 'Falha ao carregar o histórico.');
      },
    });
  }

  protected eventoLabel(tipo: string): string {
    return EVENTO_MAP[tipo] ?? tipo;
  }

  protected fmtDtHora(iso: string): string {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  protected tipoLabel(ie: string): string {
    return TIPO_MAP[ie] ?? ie;
  }

  protected descontoLabel(c: CupomPessoa): string {
    if (c.ieTipoDesconto === 'P' && c.prDesconto != null) return `${c.prDesconto}%`;
    if ((c.ieTipoDesconto === 'F' || c.ieTipoDesconto === 'R') && c.vlDesconto != null) return this.fmtR(c.vlDesconto);
    if (c.ieTipoDesconto === 'V') return 'Voucher';
    return DESC_MAP[c.ieTipoDesconto] ?? c.ieTipoDesconto;
  }

  protected vigenciaLabel(c: CupomPessoa): string {
    if (!c.dtVigenciaIni && !c.dtVigenciaFim) return 'Sem prazo';
    const ini = c.dtVigenciaIni ? this.fmtDt(c.dtVigenciaIni) : '...';
    const fim = c.dtVigenciaFim ? this.fmtDt(c.dtVigenciaFim) : '...';
    return `${ini} a ${fim}`;
  }

  protected fmtR(v: number): string {
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  protected fmtDt(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }
}
